import React, {useRef, useState} from "react";
import {pageAuthorizedByGroup} from "../fragments/PageAuthorization";
import {formatDateTimeShort} from "../../global/GlobalState";
import {Link} from "react-router-dom";
import {
	Alert,
	Breadcrumbs,
	Button,
	Paper,
	Snackbar,
	TextField,
	Typography,
	Box,
	Chip,
	Popover,
	IconButton,
	Stack
} from "@mui/material";
import {
	copyableColumn,
	textColumn,
	tooltipColumn, uuidColumn
} from "../../components/common/grid/Columns";
import {PentaSplit} from "../../components/common/PentaSplit";
import {GraphQlDataGrid, GraphQlDataGridHandle} from "../../components/common/grid/GraphQlDataGrid";
import {graphQLServices} from "../../services/GraphQLServices";
import {translateFunction} from "../../localization/TranslationService";
import {CurrentStageDisplay, HeaderSlot, StageSwitch} from "../../components/onsite/Footer";
import {ENERGY_AUCTION_REPORT_QUERY, ENERGY_AUCTION_EMAILS_QUERY, SEND_ENERGY_AUCTION_EMAILS_MUTATION} from "./Queries";
import {reportsDefaultTargetStage} from "./ReportModuleOverview";
import {useLazyQuery, useMutation} from "@apollo/client";
import {Send, FilterList} from "@mui/icons-material";

import {GridColDef} from "@mui/x-data-grid";

const translate = translateFunction("SureBasePortalEnergyAuction");

export const EnergyAuctionReport = pageAuthorizedByGroup(function () {
	return <>
		<HeaderSlot><CurrentStageDisplay/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}} to="/reports">{translate("Reports")}</Link>
				<Typography color="text.primary">{translate("Energy auction report")}</Typography>
			</Breadcrumbs>}
			center={<Paper style={{
				height: "100%",
			}}>
				<EnergyAuctionReportContent/>
			</Paper>}
		/>
	</>;
}, ["Admin","Energy"]);

export function EnergyAuctionReportContent(props: { tenant?: string }) {
	const gridRef = useRef<GraphQlDataGridHandle>(null);
	const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: "success" | "error"}>({
		open: false,
		message: "",
		severity: "success"
	});
	const [excludedEmailsText, setExcludedEmailsText] = useState<string>("");
	const [excludedEmails, setExcludedEmails] = useState<string[]>([]);
	const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLButtonElement | null>(null);

	const [fetchEmails] = useLazyQuery(ENERGY_AUCTION_EMAILS_QUERY, {
		client: graphQLServices.energyAuctionClient,
		fetchPolicy: "network-only"
	});

	const [sendEmails, {loading: sendingEmails}] = useMutation(SEND_ENERGY_AUCTION_EMAILS_MUTATION, {
		client: graphQLServices.energyAuctionClient
	});

	const handleSendEmails = async () => {
		try {
			const whereClause = gridRef.current?.graphQlWhereClause();

			// Fetch all emails with the filter
			const allEmails: string[] = [];
			let hasNextPage = true;
			let cursor: string | null = null;

			setSnackbar({
				open: true,
				message: "Fetching email addresses...",
				severity: "success"
			});

			while (hasNextPage) {
				const result: any = await fetchEmails({
					variables: {
						first: 5000,
						after: cursor,
						where: whereClause
					}
				});

				if (result.data?.energyAuctions?.nodes) {
					const emails = result.data.energyAuctions.nodes.map((node: any) => node.email);
					allEmails.push(...emails);
				}

				hasNextPage = result.data?.energyAuctions?.pageInfo?.hasNextPage ?? false;
				cursor = result.data?.energyAuctions?.pageInfo?.endCursor ?? null;
			}

			if (allEmails.length === 0) {
				setSnackbar({
					open: true,
					message: "No emails found with the current filter",
					severity: "error"
				});
				return;
			}

			// Send emails in batches of 5000
			let totalSent = 0;
			for (let i = 0; i < allEmails.length; i += 5000) {
				const batch = allEmails.slice(i, i + 5000);

				setSnackbar({
					open: true,
					message: `Sending batch ${Math.floor(i / 5000) + 1} of ${Math.ceil(allEmails.length / 5000)} (${totalSent}/${allEmails.length} sent)...`,
					severity: "success"
				});

				const sendResult: any = await sendEmails({
					variables: {
						input: {
							emails: batch
						}
					}
				});

				totalSent += sendResult.data?.sendEmails?.emailsSent ?? 0;
			}

			setSnackbar({
				open: true,
				message: `Successfully sent ${totalSent} emails out of ${allEmails.length} total`,
				severity: "success"
			});
		} catch (error: any) {
			setSnackbar({
				open: true,
				message: `Error sending emails: ${error.message}`,
				severity: "error"
			});
		}
	};

	const handleCloseSnackbar = () => {
		setSnackbar({...snackbar, open: false});
	};

	const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
		setPopoverAnchorEl(event.currentTarget);
	};

	const handleClosePopover = () => {
		setPopoverAnchorEl(null);
	};

	const handleApplyExcludedEmails = () => {
		const emails = excludedEmailsText
			.split("\n")
			.map(email => email.trim())
			.filter(email => email.length > 0 && email.includes("@"));

		setExcludedEmails(emails);
		setPopoverAnchorEl(null);
		setSnackbar({
			open: true,
			message: `Applied ${emails.length} email exclusions`,
			severity: "success"
		});
	};

	const handleClearExcludedEmails = () => {
		setExcludedEmailsText("");
		setExcludedEmails([]);
		setSnackbar({
			open: true,
			message: "Cleared email exclusions",
			severity: "success"
		});
	};

	const columns: GridColDef[] = [
		{
			field: "id",
			headerName: translate("Order ID"),
			minWidth: 330,
			renderCell: copyableColumn(tooltipColumn(uuidColumn())),
			flex: 1,
			uuid: true,
		} as any,
		{
			field: "created",
			headerName: translate("Created date"),
			type: "dateTime",
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
			valueFormatter: (params) => formatDateTimeShort(params.value),
		},
		{
			field: "lastModified",
			headerName: translate("Last Modified Date"),
			type: "dateTime",
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
			valueFormatter: (params) => formatDateTimeShort(params.value),
		},
		{
			field: "lastEmailSend",
			headerName: translate("Last Email Send Date"),
			type: "dateTime",
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
			valueFormatter: (params) => formatDateTimeShort(params.value),
		},
		
		{
			field: "email",
			headerName: translate("Email"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "transactionReference",
			headerName: translate("Transaction Reference"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "genderOrLegalPersonType",
			headerName: translate("Gender or Legal Person Type"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "initials",
			headerName: translate("Initials"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "prefixes",
			headerName: translate("Prefixes"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "surname",
			headerName: translate("Surname"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "postalCode",
			headerName: translate("Postal Code"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "houseNumber",
			headerName: translate("House Number"),
			type: "number",
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "houseNumberAddition",
			headerName: translate("House Number Addition"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "city",
			headerName: translate("City"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "street",
			headerName: translate("Street"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "tariff",
			headerName: translate("Tariff"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "sustainable",
			headerName: translate("Sustainable"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "hasSolarPanels",
			headerName: translate("HasSolarPanels"),
			type: "number",
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		
		{
			field: "gasCapacity",
			headerName: translate("Gas Capacity"),
			type: "number",
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "smartMeter",
			headerName: translate("Smart Meter"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "customerType",
			headerName: translate("Customer Type"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "supplyType",
			headerName: translate("Supply Type"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "withSavings",
			headerName: translate("With Savings"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "pricesExcludingTax",
			headerName: translate("Prices Excluding Tax"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "hideUnavailableProducts",
			headerName: translate("Hide Unavailable Products"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "productCategory",
			headerName: translate("Product Category"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "currentProvider",
			headerName: translate("Current Provider"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "electricityUsageHigh",
			headerName: translate("Electricity Usage High"),
			type: "number",
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "electricityUsageLow",
			headerName: translate("Electricity Usage Low"),
			type: "number",
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "electricityCapacity",
			headerName: translate("Electricity Capacity"),
			type: "number",
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "gasUsage",
			headerName: translate("Gas Usage"),
			type: "number",
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "electricitySupplyLow",
			headerName: translate("Electricity Supply Low"),
			type: "number",
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "electricitySupplyHigh",
			headerName: translate("Electricity Supply High"),
			type: "number",
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "doubleMeter",
			headerName: translate("Double Meter"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "applyEnergyCeiling",
			headerName: translate("Apply Energy Ceiling"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "auctionProducts",
			headerName: translate("Auction Products"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "contractType",
			headerName: translate("Contract Type"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "previousContractEndDate",
			headerName: translate("Previous Contract End Date"),
			type: "dateTime",
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
			valueFormatter: (params) => formatDateTimeShort(params.value),
		}
	];
	
	const queryVariables = excludedEmails.length > 0
		? { where: { email: { nin: excludedEmails } } }
		: undefined;

	return <>
		<GraphQlDataGrid
			ref={gridRef}
			key={`energy_auctions-${props.tenant ?? "none"}-${excludedEmails.length}`}
			rows={[]}
			query={ENERGY_AUCTION_REPORT_QUERY}
			graphFunction={"energyAuctions"}
			client={graphQLServices.energyAuctionClient}
			rowsPerPageOptions={[50, 500, 1000, 2000, 5000]}
			initialPageSize={50}
			columns={columns}
			queryVariables={queryVariables}
			defaultSortModel={[
				{
					field: "lastModified",
					sort: "desc"
				}
			]}
			initialState={{
				columns: {
					columnVisibilityModel: {
						id: false
					}
				}
			}}
			toolbarElements={
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					<Button
						variant="outlined"
						size="small"
						onClick={handleOpenPopover}
						startIcon={<FilterList />}
					>
						{translate("Exclude Emails")}
					</Button>
					{excludedEmails.length > 0 && (
						<Chip
							label={`${excludedEmails.length} excluded`}
							color="primary"
							size="small"
							onDelete={handleClearExcludedEmails}
						/>
					)}
				</Box>
			}
			customButtons={[
				<Button
					key="send-emails"
					variant="contained"
					color="primary"
					disabled={sendingEmails}
					onClick={handleSendEmails}
					startIcon={<Send />}
				>
					{sendingEmails ? "Sending..." : "Send Emails"}
				</Button>
			]}
		/>
		<Snackbar
			open={snackbar.open}
			autoHideDuration={6000}
			onClose={handleCloseSnackbar}
			anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
		>
			<Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
				{snackbar.message}
			</Alert>
		</Snackbar>
		<Popover
			open={Boolean(popoverAnchorEl)}
			anchorEl={popoverAnchorEl}
			onClose={handleClosePopover}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "left",
			}}
			transformOrigin={{
				vertical: "top",
				horizontal: "left",
			}}
		>
			<Box sx={{ p: 3, width: 500 }}>
				<Stack spacing={2}>
					<Typography variant="h6">
						{translate("Exclude Emails")}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{translate("Enter one email address per line")}
					</Typography>
					<TextField
						label={translate("Email addresses")}
						placeholder="email1@example.com&#10;email2@example.com&#10;email3@example.com"
						multiline
						rows={10}
						value={excludedEmailsText}
						onChange={(e) => setExcludedEmailsText(e.target.value)}
						fullWidth
						variant="outlined"
					/>
					<Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
						<Button
							variant="outlined"
							onClick={handleClosePopover}
						>
							{translate("Cancel")}
						</Button>
						<Button
							variant="contained"
							onClick={handleApplyExcludedEmails}
							disabled={!excludedEmailsText.trim()}
						>
							{translate("Apply")}
						</Button>
					</Box>
				</Stack>
			</Box>
		</Popover>
	</>;
}
